import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Label, FormGroup } from "reactstrap";
import { QRCodeSVG } from "qrcode.react";
import "../utility/TicketQrPrintPage.css";

const LABEL_PRESETS = {
    94107: {
        code: "94107",
        name: "Avery 94107 - 2 x 2 Waterproof (12)",
        cols: 3,
        rows: 4,
        labelW: 2.0,
        labelH: 2.0,
        marginTop: 0.58,
        marginLeft: 0.67,
        marginRight: 0.67,
        marginBottom: 0.5,
        gapX: 0.58,
        gapY: 0.6,
        qrSize: 120,
    },
    95271: {
        code: "95271",
        name: "Avery 95271 - 2.5x 2.5 (9)",
        cols: 3,
        rows: 3,
        labelW: 2.5,
        labelH: 2.5,
        marginTop: 1.5,
        marginLeft: 0.25,
        marginRight: 0.25,
        marginBottom: 1.5,
        gapX: 0.25,
        gapY: 0.25,
        qrSize: 150,
    },
    18163: {
        code: "18163",
        name: "Avery 18163 - 2 x 4 (10)",
        cols: 2,
        rows: 5,
        labelW: 4.0,
        labelH: 2.0,
        marginTop: 0.5,
        marginLeft: 0.125,
        marginRight: 0.125,
        marginBottom: 0.5,
        gapX: 0.25,
        gapY: 0.0,
        qrSize: 120,
    },
};

const DEFAULT_LABEL_CODE = "94107";

const TicketQrPrintPage = () => {
    const [startTicket, setStartTicket] = useState("1000");
    const [prefix, setPrefix] = useState("");
    const [labelCode, setLabelCode] = useState(DEFAULT_LABEL_CODE);

    const preset = LABEL_PRESETS[labelCode];
    const labelsPerPage = preset.cols * preset.rows;

    // count is always page-sized increments
    const [count, setCount] = useState(String(labelsPerPage));

    // reset to one full page whenever label style changes
    useEffect(() => {
        setCount(String(labelsPerPage));
    }, [labelsPerPage]);

    const maxPages = Math.max(1, Math.floor(200 / labelsPerPage));
    const countOptions = Array.from({ length: maxPages }, (_, i) => String((i + 1) * labelsPerPage));

    const safeStart = String(startTicket || "").replace(/\D/g, "");
    const safeCount = Number(count) || labelsPerPage;

    const tickets = [];
    const startNum = parseInt(safeStart || "0", 10);
    for (let i = 0; i < safeCount; i += 1) {
        tickets.push(`${prefix}${startNum + i}`);
    }

    // split into one printable sheet per page
    const pages = [];
    for (let i = 0; i < tickets.length; i += labelsPerPage) {
        pages.push(tickets.slice(i, i + labelsPerPage));
    }

    const sheetStyle = useMemo(
        () => ({
            "--label-cols": String(preset.cols),
            "--label-w": `${preset.labelW}in`,
            "--label-h": `${preset.labelH}in`,
            "--sheet-margin-top": `${preset.marginTop}in`,
            "--sheet-margin-left": `${preset.marginLeft}in`,
            "--sheet-margin-right": `${preset.marginRight}in`,
            "--sheet-margin-bottom": `${preset.marginBottom}in`,
            "--label-gap-x": `${preset.gapX}in`,
            "--label-gap-y": `${preset.gapY}in`,
        }),
        [preset],
    );

    return (
        <section className="ticket-print-page page-scroll">
            <div className="ticket-print-inner page-scroll-inner">
                <div className="ticket-print-controls no-print">
                    <h3>Print Ticket QR Codes</h3>

                    <div className="ticket-print-control-grid">
                        <FormGroup className="ticket-print-control-item">
                            <Label for="labelCode">Label Style</Label>
                            <Input
                                id="labelCode"
                                type="select"
                                value={labelCode}
                                onChange={(e) => setLabelCode(e.target.value)}>
                                {Object.values(LABEL_PRESETS).map((opt) => (
                                    <option
                                        key={opt.code}
                                        value={opt.code}>
                                        {opt.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup className="ticket-print-control-item">
                            <Label for="startTicket">Start Ticket Number</Label>
                            <Input
                                id="startTicket"
                                type="tel"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                value={startTicket}
                                onChange={(e) => setStartTicket(String(e.target.value || "").replace(/\D/g, ""))}
                            />
                        </FormGroup>

                        <FormGroup className="ticket-print-control-item">
                            <Label for="count">How Many</Label>
                            <Input
                                id="count"
                                type="select"
                                value={count}
                                onChange={(e) => setCount(e.target.value)}>
                                {countOptions.map((opt) => (
                                    <option
                                        key={opt}
                                        value={opt}>
                                        {opt}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>

                        <FormGroup className="ticket-print-control-item">
                            <Label for="prefix">Optional Prefix</Label>
                            <Input
                                id="prefix"
                                type="text"
                                value={prefix}
                                onChange={(e) => setPrefix(e.target.value)}
                                placeholder="ex: A-"
                            />
                        </FormGroup>
                    </div>

                    <div className="ticket-print-actions">
                        <Button
                            color="primary"
                            onClick={() => window.print()}>
                            Print Tickets
                        </Button>
                    </div>
                </div>

                {pages.map((pageTickets, pageIdx) => (
                    <div
                        key={`sheet-${pageIdx}`}
                        className="ticket-sheet"
                        style={sheetStyle}>
                        {pageTickets.map((ticket) => (
                            <div
                                key={ticket}
                                className="ticket-card">
                                <div className="ticket-num">{ticket}</div>
                                <QRCodeSVG
                                    value={`PP:TICKET:${String(ticket).replace(/\D/g, "")}`}
                                    size={preset.qrSize}
                                    level="H"
                                    fgColor="#000000"
                                    bgColor="#ffffff"
                                    marginSize={2}
                                />
                            </div>
                        ))}
                    </div>
                ))}

                <div className="ticket-print-tips no-print">
                    <h6>Printing Tips</h6>
                    <ul>
                        <li>Use Actual Size (100 percent scale).</li>
                        <li>Turn off Fit to Page / Shrink to Fit.</li>
                        <li>Use Letter, Portrait.</li>
                        <li>Print a plain-paper alignment test first.</li>
                        <li>If needed, adjust top/left margins slightly for your printer.</li>
                    </ul>
                </div>
            </div>
        </section>
    );
};

export default TicketQrPrintPage;
